import { protectedResources } from '../../authConfig';
import DocumentType from '../../util/enums/DocumentType';
import formatDate from '../../util/formatter/formatDate';
import makeGetRequestAsync, {
  downloadFileAsync,
  makeGetBlobRequestAsync,
  makePostFormRequestAsync,
  makePostRequestAsync
} from '../Helpers/httpRequestHelpers';
import { DocumentTypeModel } from '../Redux/Models/DocumentTypeModel';
import { DocumentDtoModel } from './Models/DocumentDtoModel';
import { DocumentModel } from './Models/DocumentModel';

const apiScopes = protectedResources.documentManagementWebApi.scopes;
const baseUrl = protectedResources.documentManagementWebApi.endpoint;

const getCompanyDocumentDownloadUrl = (documentId: string) =>
  `${baseUrl}/api/Documentation/DownloadCompanyDocument/${documentId}`;

/*
 *  fetch a company document as a Blob via the authenticated download endpoint
 */
export const fetchCompanyDocumentBlobAsync = async (
  documentId: string
): Promise<Blob> => {
  const url = getCompanyDocumentDownloadUrl(documentId);
  const response = await makeGetBlobRequestAsync(apiScopes, url);
  return response.data;
};

/*
 *  download a company document via the authenticated download endpoint
 */
export const downloadCompanyDocumentAsync = async (
  documentId: string,
  fileName?: string
) => {
  const url = getCompanyDocumentDownloadUrl(documentId);
  await downloadFileAsync(apiScopes, url, fileName);
};

export const getCompanyRequiredDocumentTypes = async () => {
  const url = `${baseUrl}/api/Documentation/GetAllCompanyRequiredDocTypes`;
  return makeGetRequestAsync<Array<DocumentTypeModel>>(apiScopes, url);
};

export const getAllRequiredDocumentTypes = async () => {
  const url = `${baseUrl}/api/Documentation/GetAllRequiredDocTypes`;
  return makeGetRequestAsync<Array<DocumentTypeModel>>(apiScopes, url);
};

export const getCompanyDocumentAsync = async (documentId: string) => {
  const url = `${baseUrl}/api/Documentation/GetCompanyDocument/${documentId}`;
  return makeGetRequestAsync<DocumentModel>(apiScopes, url);
};

export const getShareHolderDocumentByIdAsync = async (documentId: string) => {
  const url = `${baseUrl}/api/Documentation/GetCompanyDocument/${documentId}`;
  return makeGetRequestAsync<DocumentDtoModel>(apiScopes, url);
};

export const getCompanyDocumentByTypeIdAsync = async (
  companyId: number,
  documentTypeId: number
) => {
  const url = `${baseUrl}/api/Documentation/GetCompanyDocumentByType/${companyId}/${documentTypeId}`;
  return (await makeGetRequestAsync<DocumentDtoModel>(apiScopes, url)).data;
};

export const getCompanyDocumentsWithTypesAsync = async (
  companyId: number | undefined
) => {
  const url = `${baseUrl}/api/Documentation/GetCompanyDocumentsWithTypes/${companyId}`;
  return makeGetRequestAsync<Array<DocumentDtoModel>>(apiScopes, url);
};

export const addNewMasterSupplierDocumentAsync = async (
  clientId: number,
  documentFiles: File[]
) => {
  const formData = new FormData();
  formData.append('clientId', clientId.toString());
  formData.append('file', documentFiles[0]!);

  const url = `${baseUrl}/api/Documentation/AddNewMasterSupplierDocument`;

  return makePostFormRequestAsync(apiScopes, url, formData);
};

export const addNewCompanyDocumentAsync = async (
  companyId: number,
  documentExpires: boolean,
  expiryDate: Date,
  typeId: number | undefined | null,
  userId: string | undefined,
  documentFiles: File[],
  workCentreId?: number,
  documentTypeEnum: number = DocumentType.Unknown
) => {
  const formData = new FormData();
  formData.append('companyId', companyId.toString());
  formData.append('documentExpires', documentExpires ? 'true' : 'false');

  if (documentExpires) formData.append('expiryDate', formatDate(expiryDate));

  formData.append('typeId', typeId?.toString() || '');
  formData.append('userId', userId?.toString() || '');
  formData.append('workCentreId', workCentreId?.toString() || '');
  formData.append('documentTypeEnum', documentTypeEnum.toString());
  formData.append('file', documentFiles[0]!);

  const url = `${baseUrl}/api/Documentation/AddNewDocument`;

  return (await makePostFormRequestAsync<string, any>(apiScopes, url, formData)).data;
};

export const deleteCompanyDocumentAsync = async (
  companyId: number,
  documentId: string,
  userId: string
) => {
  const url = `${baseUrl}/api/Documentation/DeleteDocument`;

  const data = {
    companyId,
    documentId,
    userId
  };

  return makePostRequestAsync(apiScopes, url, data);
};

/*
 *  download supplier upload template
 */
export async function downloadSupplierUploadTemplate() {
  const downloadUrl = `${baseUrl}/api/documentation/SupplierUploadTemplate`;
  await downloadFileAsync(apiScopes, downloadUrl);
}

/*
 *  download supplier upload template for platfrom data admi
 */
export async function downloadSupplierUploadDataPlatormAdminTemplate() {
  const downloadUrl = `${baseUrl}/api/documentation/SupplierUploadTemplate/platformdatatemplate`;
  await downloadFileAsync(apiScopes, downloadUrl);
}

/*
 *  download master supplier upload template
 */
export async function downloadMasterupplierUploadTemplate() {
  const downloadUrl = `${baseUrl}/api/documentation/SupplierUploadTemplate/mastersuppliertemplate`;
  await downloadFileAsync(apiScopes, downloadUrl);
}

/*
 *  upload master bee documents
 */
export const uploadMasterBeeDocument = async (
  masterCompanyRecordId: number,
  documentFiles: File[]
) => {
  const formData = new FormData();
  formData.append('masterCompanyRecordId', masterCompanyRecordId.toString());
  formData.append('file', documentFiles[0]!);

  const url = `${baseUrl}/api/Documentation/UploadMasterBeeDocument`;

  return makePostFormRequestAsync(apiScopes, url, formData);
};
/*
 *  upload a configured document template (e.g. agreement .docx templates).
 *  Routes to the purpose-built endpoint that uses
 *  DocumentTypeEnum.OnboardingWorkflowConfiguredDocument, so .docx/.doc files
 *  are not rejected by the system-standard PDF-only file type validator.
 */
export const uploadAgreementTemplateDocumentAsync = async (documentFiles: File[]) => {
  const formData = new FormData();
  formData.append('file', documentFiles[0]!);

  const url = `${baseUrl}/api/Documentation/UploadConfiguredDocumentTemplate`;

  // returns uploaded documentId
  return (await makePostFormRequestAsync<string, FormData>(apiScopes, url, formData))
    .data;
};

/*
 *  upload shareholder document
 */
export const addNewCompanyShareholderDocumentAsync = async (documentFiles: File[]) => {
  const formData = new FormData();
  formData.append('file', documentFiles[0]!);

  const url = `${baseUrl}/api/Documentation/AddNewShareholderDocument`;

  //rturns uploaded documentId
  return makePostFormRequestAsync<string, FormData>(apiScopes, url, formData);
};
